import React, { useEffect, useMemo, useState } from 'react';
import { projectFirestore } from '../../firebase/config';

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatDate(value) {
  const ms = toMillis(value);
  if (!ms) return 'غير متاح';
  return new Date(ms).toLocaleString('ar-EG');
}

function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 8) {
  const words = String(text || '').split(' ');
  let line = '';
  const lines = [];

  for (let i = 0; i < words.length; i += 1) {
    const next = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = words[i];
    } else {
      line = next;
    }
  }

  if (line) lines.push(line);

  lines.slice(0, maxLines).forEach((lineText, idx) => {
    ctx.fillText(lineText, x, y + idx * lineHeight);
  });
}

function createSingleFeedbackImageBlob(item) {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 1200;
  const ctx = canvas.getContext('2d');

  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#0f62fe');
  gradient.addColorStop(1, '#0043ce');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'right';
  ctx.direction = 'rtl';

  ctx.font = '700 58px Tajawal, sans-serif';
  ctx.fillText('تعليق من لوحة الإدارة - حكاية ضوء', 1120, 120);

  ctx.font = '700 48px Tajawal, sans-serif';
  const stars = '★'.repeat(Number(item.rating) || 0);
  ctx.fillText(`${stars} (${Number(item.rating) || 0}/5)`, 1120, 220);

  ctx.font = '500 44px Tajawal, sans-serif';
  drawWrappedText(ctx, item.comment || item.message, 1120, 320, 960, 70);

  ctx.font = '600 36px Tajawal, sans-serif';
  ctx.fillText(`- ${item.name || 'زائر'}`, 1120, 980);

  ctx.globalAlpha = 0.9;
  ctx.font = '500 30px Tajawal, sans-serif';
  ctx.fillText('reactgallery-2021.web.app', 1120, 1060);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function createGroupFeedbackImageBlob(items) {
  const safeItems = items.slice(0, 8);
  const cardHeight = 185;
  const topOffset = 180;
  const bottomOffset = 90;
  const width = 1400;
  const height = topOffset + (safeItems.length * cardHeight) + bottomOffset;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#1a365d');
  gradient.addColorStop(1, '#2a4a7f');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  ctx.textAlign = 'right';
  ctx.direction = 'rtl';
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 52px Tajawal, sans-serif';
  ctx.fillText('تعليقات مميزة من لوحة الإدارة', width - 80, 85);

  ctx.font = '500 28px Tajawal, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fillText(`إجمالي التعليقات في الصورة: ${safeItems.length}`, width - 80, 135);

  safeItems.forEach((item, index) => {
    const y = topOffset + (index * cardHeight);

    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(50, y - 40, width - 100, 155);

    ctx.fillStyle = '#ffffff';
    ctx.font = '700 30px Tajawal, sans-serif';
    const stars = '★'.repeat(Number(item.rating) || 0);
    ctx.fillText(`${item.name || 'زائر'} - ${stars}`, width - 90, y);

    ctx.font = '500 26px Tajawal, sans-serif';
    drawWrappedText(ctx, item.comment || item.message || '', width - 90, y + 46, width - 180, 40, 2);
  });

  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = '500 24px Tajawal, sans-serif';
  ctx.fillText('reactgallery-2021.web.app', width - 80, height - 28);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

const FeedbackManager = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sharingOneId, setSharingOneId] = useState('');
  const [sharingGroup, setSharingGroup] = useState(false);
  const [shareNotice, setShareNotice] = useState('');

  useEffect(() => {
    const unsub = projectFirestore.collection('siteFeedback').onSnapshot((snap) => {
      const list = [];
      snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
      list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      setFeedbacks(list);
      setLoading(false);
    }, () => {
      setFeedbacks([]);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return feedbacks;
    return feedbacks.filter((f) => (f.status || 'pending') === statusFilter);
  }, [feedbacks, statusFilter]);

  const selectedFromFiltered = useMemo(() => {
    return filtered.filter((item) => selectedIds.includes(item.id));
  }, [filtered, selectedIds]);

  const updateStatus = async (id, nextStatus) => {
    await projectFirestore.collection('siteFeedback').doc(id).update({
      status: nextStatus,
      updatedAt: new Date().toISOString(),
    });
  };

  const removeFeedback = async (id) => {
    if (!window.confirm('هل تريد حذف هذا الرأي؟')) return;
    await projectFirestore.collection('siteFeedback').doc(id).delete();
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(filtered.map((item) => item.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const shareBlob = async (blob, fileName, fallbackNotice) => {
    if (!blob) throw new Error('empty-blob');
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'مشاركة تعليقات',
        text: 'تعليقات من لوحة إدارة الموقع',
      });
      setShareNotice('تمت المشاركة بنجاح.');
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setShareNotice(fallbackNotice);
  };

  const shareOne = async (item) => {
    setSharingOneId(item.id);
    setShareNotice('');
    try {
      const blob = await createSingleFeedbackImageBlob(item);
      await shareBlob(blob, `feedback-${item.id}.png`, 'تم تنزيل صورة التعليق.');
    } catch (error) {
      setShareNotice('تعذر مشاركة التعليق حالياً.');
    }
    setSharingOneId('');
  };

  const shareGroup = async (items, label) => {
    if (!items.length) {
      setShareNotice('لا توجد تعليقات للمشاركة.');
      return;
    }

    setSharingGroup(true);
    setShareNotice('');
    try {
      const blob = await createGroupFeedbackImageBlob(items);
      await shareBlob(blob, `feedback-group-${Date.now()}.png`, `تم تنزيل صورة ${label}.`);
    } catch (error) {
      setShareNotice('تعذر مشاركة مجموعة التعليقات حالياً.');
    }
    setSharingGroup(false);
  };

  if (loading) {
    return <div className="text-center py-4"><div className="spinner-border text-primary" /></div>;
  }

  return (
    <div className="container my-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h5 className="mb-0">إدارة التقييمات وآراء المستخدمين</h5>
        <div className="d-flex gap-1 flex-wrap">
          <button className={`btn btn-sm ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setStatusFilter('all')}>الكل</button>
          <button className={`btn btn-sm ${statusFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => setStatusFilter('pending')}>معلقة</button>
          <button className={`btn btn-sm ${statusFilter === 'published' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setStatusFilter('published')}>منشورة</button>
          <button className={`btn btn-sm ${statusFilter === 'hidden' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setStatusFilter('hidden')}>مخفية</button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div className="small text-muted">
            تحديد التعليقات: {selectedFromFiltered.length} من {filtered.length}
          </div>
          <div className="d-flex flex-wrap gap-1">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllFiltered} disabled={filtered.length === 0}>
              تحديد الكل (الفلتر الحالي)
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearSelection} disabled={selectedIds.length === 0}>
              إلغاء التحديد
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => shareGroup(filtered, 'التعليقات المفلترة')}
              disabled={sharingGroup || filtered.length === 0}
            >
              {sharingGroup ? 'جارٍ التجهيز...' : 'مشاركة المفلتر كصورة'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => shareGroup(selectedFromFiltered, 'التعليقات المحددة')}
              disabled={sharingGroup || selectedFromFiltered.length === 0}
            >
              {sharingGroup ? 'جارٍ التجهيز...' : 'مشاركة المحدد كصورة'}
            </button>
          </div>
        </div>
      </div>

      {shareNotice && <div className="alert alert-info py-2">{shareNotice}</div>}

      {filtered.length === 0 ? (
        <div className="alert alert-light border">لا توجد آراء مطابقة لهذا الفلتر.</div>
      ) : (
        <div className="row g-3">
          {filtered.map((item) => (
            <div className="col-12 col-lg-6" key={item.id}>
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-start gap-2">
                      <input
                        type="checkbox"
                        className="form-check-input mt-1"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        aria-label="تحديد التعليق"
                      />
                      <div>
                        <h6 className="mb-0">{item.name || 'زائر'}</h6>
                        <small className="text-muted">{formatDate(item.createdAt)}</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span className={`badge ${item.status === 'published' ? 'bg-success' : item.status === 'hidden' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                        {item.status === 'published' ? 'منشور' : item.status === 'hidden' ? 'مخفي' : 'معلق'}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => shareOne(item)}
                        disabled={sharingOneId === item.id}
                      >
                        {sharingOneId === item.id ? 'جارٍ التجهيز...' : 'مشاركة كصورة'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-2" style={{ fontSize: 15 }}>
                    {'★'.repeat(Number(item.rating) || 0)}
                    <span className="text-muted ms-2">({Number(item.rating) || 0}/5)</span>
                  </div>

                  <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{item.comment || item.message}</p>

                  <div className="d-flex flex-wrap gap-1">
                    <button className="btn btn-sm btn-success" onClick={() => updateStatus(item.id, 'published')}>اعتماد ونشر</button>
                    <button className="btn btn-sm btn-warning" onClick={() => updateStatus(item.id, 'pending')}>إعادة للتعليق</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => updateStatus(item.id, 'hidden')}>إخفاء</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeFeedback(item.id)}>حذف</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackManager;
